/**
 * The shape of `POST /declarations/{id}/approve`'s 409 `detail`, as it
 * ACTUALLY arrives — snake_case, because `apiClientV2`'s response
 * interceptor only camelCases the success path; an error body passes
 * through untouched (see `declarationInquiriesApi.ts`'s identical note on
 * `inquiry_id`). The existing `ApproveErrorDetail`/`ApprovePendingBreakdown`
 * in `api/types.ts` are typed camelCase and are not read by any call site
 * today (confirmed by grep) — they are left untouched rather than "fixed",
 * since correcting a type nothing consumes is out of this task's scope and
 * risks a name collision with a future real usage. This is Studio V2's own,
 * correctly-spelled reading of the same wire payload.
 *
 * `OPEN_INQUIRY` mirrors the shipped backend contract: `approve()` in
 * `mc_declaration_review_service.py` (merged via 5c14e82f) raises this exact
 * 409 shape — `code`, `message`, and `open_inquiries` with `framework_slug`/
 * `inquiry_id`/`sent_at`/`status` per row. The panel below still tolerates a
 * missing or empty `open_inquiries` rather than assuming the array is always
 * populated.
 */
export interface ApproveBlockingWorkItem {
  step: 1 | 2 | 3
  state: "needs-input" | "needs-verification" | "ready"
  framework: string
  unit: "statement" | "framework"
  unit_id: string
  statement_id: string | null
  group_index: number | null
  article_numbers: string[]
}

export interface ApproveOpenInquiryItem {
  /** Null for a declaration question inquiry (TUR-1211 wave 5), which is
   *  raised against the whole declaration, not one framework. */
  framework_slug: string | null
  inquiry_id: string
  sent_at: string | null
  /** Raw inquiry status — "waiting"/"reminded" render the normal open
   *  wording; "undeliverable"/"complained" render the dead-address wording
   *  (plan EXECUTION STATUS note: the BE fix partitions this per row). */
  status: string
}

export interface ApproveMissingListVersionStatement {
  statement_id: string
  framework: string
  group_index: number
  named: string
}

/**
 * Stage 1 (2026-09-17 decision): a supplier "question batch" (wave 5
 * collector, `source_framework NULL`) no longer blocks approve outright, it
 * only refuses an UNACKNOWLEDGED approve, this shape names why. `status` is
 * the raw inquiry status, same open/dead-address split `ApproveOpenInquiryItem`
 * uses (open: waiting/reminded/escalated/ureview; dead-address:
 * undeliverable/complained).
 */
export interface ApproveOpenQuestionBatch {
  inquiry_id: string
  status: string
  sent_at: string | null
  question_count: number
}

export type ApproveGateCode =
  | "PENDING_ITEMS"
  | "OPEN_INQUIRY"
  | "REVIEW_PENDING"
  | "RECOMPUTE_RUNNING"
  | "RECOMPUTE_DROPPED"
  | "RECOMPUTE_FAILED"
  | "ALREADY_APPROVED"
  | "NO_SUPPLIER"
  | "SUPPLIER_CHANGED"
  | "MISSING_LIST_VERSION"
  | "NOT_PROCESSED"
  | "OPEN_QUESTIONS_UNACKNOWLEDGED"
  | string

/** The four counters `approve`'s PENDING_ITEMS refusal carries (`blockers` in
 *  `mc_declaration_review_service.py`). `unresolved_review` is `blocking_work`'s
 *  own length, so it can never be non-zero while that array is empty. The
 *  other three can, which is the whole reason the panel reads this. */
export interface ApprovePendingCounters {
  interface_gaps?: number
  missing_cas_claims?: number
  unresolved_conflicts?: number
  unresolved_review?: number
}

export interface ApproveGateDetail {
  code: ApproveGateCode
  message?: string
  pending?: ApprovePendingCounters
  blocking_work?: ApproveBlockingWorkItem[]
  open_inquiries?: ApproveOpenInquiryItem[]
  review_started?: boolean
  /** REVIEW_PENDING only (TUR-1725, `MC_REVIEW_ON_WORKER`): the final review is
   *  queued on the worker but no worker has taken it within its limits. */
  review_delayed?: boolean
  /** RECOMPUTE_RUNNING only (TUR-1725, `MC_REVIEW_ON_WORKER`): nothing was
   *  recalculating; the declaration changed between approve's checks and its
   *  write (a gesture or a cone publish committed in between). The same
   *  retryable wait, worded as what happened. */
  gates_moved?: boolean
  /** RECOMPUTE_RUNNING only (TUR-1725): approve could not take the declaration
   *  row in time (a save, a sign-off or a recalculation held it) or lost a
   *  deadlock. Nothing changed and nothing was written: the declaration was only
   *  busy. The same retryable wait, worded as such. */
  review_busy?: boolean
  statements?: ApproveMissingListVersionStatement[]
  open_questions?: ApproveOpenQuestionBatch[]
}

/** Reads the 409's `detail` off an axios-shaped error, or null when the
 *  error carries none (a network failure, a 5xx already toasted by the
 *  interceptor, …). */
export function approveGateDetailOf(error: unknown): ApproveGateDetail | null {
  const detail = (
    error as { response?: { data?: { detail?: ApproveGateDetail } } }
  )?.response?.data?.detail
  return detail?.code ? detail : null
}
